import type { FC } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as Logo } from 'shared/img/logo.svg'
import { ReactComponent as Logout } from 'shared/img/logout.svg'
import refreshIcon from 'shared/img/refresh.png'

import { usePromotionsCount } from 'shared/hooks/usePromotionsCount'
import { BonusPromotionStatus } from 'shared/models/Promotion'
import { useMediaDevice, useModalContext } from '@bcraft-frontend-lib/core'

import { actions as authActions } from 'features/auth'
import { selectIsUserAuthenticated, selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { navigationItems } from 'components/NavigationHeader/data'
import NavigationItem from 'components/NavigationHeader/NavigationItem/NavigationItem'
import { SetLanguage } from 'components/SetLanguage/SetLanguage'
import { ButtonSizes, Icon } from 'components/UIKit'
import { Button, ButtonVariants } from 'components/UIKit/Button/Button'

import { ReactComponent as Profile } from './img/profile.svg'
import { ReactComponent as PromoGift } from './img/headerGift.svg'

import './Header.scss'

const b = block('header')

export type HeaderProps = {
  onToggleLeftMenu: () => void
  onToggleRightMenu: () => void
}

export const Header: FC<HeaderProps> = ({ onToggleLeftMenu, onToggleRightMenu }) => {
  const { pushNewModal } = useModalContext()
  const headerRef = useRef<HTMLElement | null>(null)
  const dispatch = useDispatch()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeAuth = useSelector(selectLocaleDict, shallowEqual).auth
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const user = useSelector(selectUserData, shallowEqual)
  const isMobileOrTablet = useMediaDevice({ query: 'maxLaptop' })
  const history = useHistory()
  const { count: promosCount } = usePromotionsCount()
  const promotionsRoute = ROUTE_URLS.PROFILE_PROMOTIONS_STATUS(BonusPromotionStatus.available)

  const calcHeaderHeight = () => {
    document.body.style.setProperty('--header-height', `${headerRef.current?.clientHeight}px`)
  }

  useEffect(() => {
    calcHeaderHeight()

    let observer: ResizeObserver

    if (headerRef.current) {
      observer = new ResizeObserver(calcHeaderHeight)

      observer.observe(headerRef.current)
    }

    return () => observer?.disconnect()
  }, [])

  const handleLogout = useCallback(() => {
    pushNewModal(MODAL_ROUTE_URLS.LOGOUT())()
  }, [pushNewModal])

  const handleRefreshBalance = useCallback(() => {
    dispatch(authActions.getUserData())
  }, [dispatch])

  return (
    <header ref={headerRef} className={b()}>
      <div className={b('inner')}>
        {isMobileOrTablet && onToggleLeftMenu && (
          <button
            type="button"
            onClick={() => onToggleLeftMenu()}
            className={b('menu-trigger', { side: 'left' })}
            aria-label="Open menu">
            <Icon type="bars" />
          </button>
        )}

        <Link className={b('logo-wrapper')} to={ROUTE_URLS.MAIN()}>
          <Logo className={b('logo')} />
        </Link>

        <div className={b('navigation')}>
          {navigationItems
            .filter(it => !it.isMobile)
            .map(navItem => (
              <NavigationItem key={navItem.route} {...navItem} />
            ))}
        </div>

        <div className={b('lang', { auth: isAuth })}>
          <SetLanguage />
        </div>

        {isAuth && (
          <>
            <div className={b('balance-wrapper')}>
              <div className={b('balance-list')}>
                <div className={b('balance')}>
                  <span className={b('balance-amount')}>{locale.balance}: </span>
                  <div className={b('balance-value')}>
                    <span>{user?.balance}</span>
                    <span className={b('balance-currency')}>{user?.currency}</span>
                  </div>
                </div>
                <div className={b('balance')}>
                  <span className={b('balance-amount')}>{locale.bonus}: </span>
                  <div className={b('balance-value')}>
                    <span>{user?.bonusBalance.toFixed(2)}</span>
                    <span className={b('balance-currency')}>B</span>
                  </div>
                </div>
              </div>
              <button
                className={b('balance-refresh')}
                type="button"
                onClick={handleRefreshBalance}
                aria-label="Refresh balance">
                <img className={b('balance-refresh-icon')} src={refreshIcon} alt="" aria-hidden />
              </button>
            </div>
          </>
        )}

        {!isAuth && (
          <div className={b('auth-buttons')}>
            <Button
              size={ButtonSizes.MEDIUM}
              className={b('login-button')}
              variant={ButtonVariants.SECONDARY}
              uppercase
              onClick={pushNewModal(MODAL_ROUTE_URLS.SIGN_UP())}>
              {localeAuth.signUp}
            </Button>
            <Button
              className={b('login-button')}
              size={ButtonSizes.MEDIUM}
              fullWidth={isMobileOrTablet}
              uppercase
              onClick={pushNewModal(MODAL_ROUTE_URLS.SIGN_IN())}>
              {locale.logIn}
            </Button>
          </div>
        )}

        {isAuth && (
          <>
            {!isMobileOrTablet && (
              <Link className={b('promo-gift')} to={promotionsRoute} aria-label={locale.promotions}>
                <PromoGift />
                {promosCount > 0 && <span className={b('promo-gift-count')}>{promosCount}</span>}
              </Link>
            )}
            <button
              className={b('profile')}
              onClick={() => {
                if (!isMobileOrTablet) {
                  history.push(ROUTE_URLS.PROFILE_USERINFO())
                }

                onToggleRightMenu()
              }}>
              <Profile />
            </button>
            {!isMobileOrTablet && (
              <button className={b('logout')} onClick={handleLogout}>
                <Logout />
              </button>
            )}
          </>
        )}
      </div>
    </header>
  )
}
